import { useDecisionLog } from '../../api/hooks';
import { loadErrorLine } from '../../api/errors';
import { decisionActorClause } from '../../lib/copy';
import { formatDay, joinClauses } from '../../lib/format';
import { Loading } from '../ui/loading';
import { EmptyLine } from '../ui/empty-line';

/**
 * The decision log (ui-design §4.7 region 7): answered and recorded events as
 * one quiet audit list. Every gesture in this design is a logged decision rather
 * than UI state, and this is where that shows.
 *
 * `summary` is the server's plain-language line; the client never composes one,
 * which is how the words negate / supersede / anti-anchor / lineage stay out.
 */
export function DecisionLog({ subjectId }: { subjectId: string }) {
  const log = useDecisionLog(subjectId);

  if (log.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (log.isError) return <EmptyLine>{loadErrorLine(log.error)}</EmptyLine>;
  if (log.data.entries.length === 0) return <EmptyLine>Nothing logged yet.</EmptyLine>;

  return (
    <div>
      {log.data.entries.map((row) => (
        <div className="act-row" key={row.id}>
          <div className="act-value fill">{row.summary}</div>
          <div className="act-state">
            {joinClauses([
              /* **Who, through what, and why** — the member's own name as
                 before, then the channel when the answer came through the
                 connector, then the words they gave for it
                 (reconcile-through-the-connector note §3). A dashboard answer
                 has neither and renders exactly as it did. */
              decisionActorClause(
                row.answered_by?.name ?? (row.actor === 'user' ? null : 'automatic'),
                row.answered_via,
                row.answer_reason,
              ),
              formatDay(row.answered_at ?? row.created_at),
            ])}
          </div>
        </div>
      ))}
    </div>
  );
}
